import { clsx } from "clsx";
import { get, set, uniqueId } from "lodash-es";
import { type JSX, PureComponent, useEffect, useState } from "react";
import { useMenuTriggerState } from "react-stately";
import { type InjectedFormProps, reduxForm } from "redux-form";
import {
  ActionButton,
  ActionButtonIntent,
  ActionButtonSize,
  MenuItem,
  MenuItemIntent,
  MenuSection,
  parseDateToISO,
} from "widgets";
import { isUndefinedOrEmptyArray } from "widgets/utils";

import { isProfileFilterColumn } from "@/app-utils/filter.ts";
import {
  CONSTRAINT_OPERATORS,
  type IRsqlComparisonExpr,
  type IRsqlFilterTree,
  isIRsqlComparisonExpr,
  isIRsqlFilterTree,
  parse,
  type RsqlFilterConstraint,
  SEARCH_OPERATORS,
} from "@/app-utils/rsql.ts";
import { type InputTableQuerySpec } from "@/graphql.ts";
import { type ITableColumn } from "@/redux/modules/table-data.ts";
import { CELL_TYPE, ENTITY_TYPE } from "@/settings/settings.ts";

import AdvancedSearchCriteria, {
  AdvancedSearchCriteriaReduxFormField,
  validateFilterOperation,
} from "../forms/AdvancedSearchCriteria.js";
import { MenuControlledOverlay, MenuDialog } from "../overlays/index.js";
import style from "./styles/ColumnMenu.module.scss";

// --- Overlay ---
interface IColumnMenuOverlayProps {
  onFilter(columnField: string, filter: RsqlFilterConstraint);
  onRemoveFilter(columnField: string);
  onClose();
  onAdvancedSortClick();
  onColumnSelectionClick(activeColumn: ITableColumn);
  onHideColumn(column: ITableColumn, hidden: boolean);
  onToggleColumnHeadersWrapping(wrapColumnHeader: boolean): void;

  columnMenuData: IColumnMenuData;
  overlayColumnMenuOnTop?: boolean;
  wrapColumnHeaders: boolean;
  tableQuerySpec: InputTableQuerySpec;
}

// Column filters live on the column, but widgets/views often only persist the combined table rowFilter.
// Extract the portion of a global filter that targets the active column so the menu can prefill it.
const getFilterForField = (
  filterOperation: RsqlFilterConstraint | undefined,
  field: string,
): RsqlFilterConstraint | undefined => {
  if (!filterOperation) {
    return undefined;
  }

  if (isIRsqlComparisonExpr(filterOperation)) {
    return filterOperation.key === field ? filterOperation : undefined;
  }

  if (isIRsqlFilterTree(filterOperation)) {
    const logicalOperators = [CONSTRAINT_OPERATORS.AND, CONSTRAINT_OPERATORS.OR];
    for (const operator of logicalOperators) {
      const children = (filterOperation as IRsqlFilterTree)[operator] as RsqlFilterConstraint[];
      if (!children?.length) {
        continue;
      }

      const matchingChildren = children
        .map((child) => getFilterForField(child, field))
        .filter(Boolean) as RsqlFilterConstraint[];

      // If the whole logical branch targets this field, keep it intact; otherwise fall back to the first
      // matching child so we can at least prefill the field-specific part instead of showing nothing
      // (the menu only supports a single constraint per column).
      if (matchingChildren.length === children.length) {
        return { [operator]: matchingChildren };
      }

      if (matchingChildren.length > 0) {
        return matchingChildren[0];
      }
    }
  }

  return undefined;
};

function ColumnMenuOverlay(props: IColumnMenuOverlayProps): JSX.Element | null {
  const [, forceUpdate] = useState(uniqueId());
  const state = useMenuTriggerState({
    isOpen: !!props.columnMenuData,
  });
  const triggerRef = props.columnMenuData?.menuTarget || { current: undefined };

  // Close the menu on window resize
  useEffect(() => {
    const close = () => props.onClose();
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("resize", close);
    };
  }, [props]);

  if (!props.columnMenuData?.menuTarget) return null;

  return state.isOpen ? (
    <MenuControlledOverlay
      key={`menu-overlay-${props.columnMenuData.activeColumn.field}`}
      isOpen={state.isOpen}
      close={state.close}
      targetRef={triggerRef}
      onClose={props.onClose}
      onCancel={props.onClose}
      shouldCloseOnBlur
      isDismissable
    >
      {() => (
        <ColumnContextualMenu
          columnMenuData={props.columnMenuData}
          tableQuerySpec={props.tableQuerySpec}
          wrapColumnHeaders={props.wrapColumnHeaders}
          onFilter={props.onFilter}
          onAdvancedSortClick={props.onAdvancedSortClick}
          onRemoveFilter={props.onRemoveFilter}
          onColumnSelectionClick={props.onColumnSelectionClick}
          onHideColumn={props.onHideColumn}
          onToggleColumnHeadersWrapping={props.onToggleColumnHeadersWrapping}
          refreshPopover={() => forceUpdate(uniqueId())}
        />
      )}
    </MenuControlledOverlay>
  ) : null;
}

// --- Menu ---
const COLUMN_FILTER_FORM = "columnFilter";

export interface IColumnMenuData {
  activeColumn: ITableColumn;
  menuTarget: any;
}

interface IColumnMenuProps {
  onFilter: (columnField: string, filter: RsqlFilterConstraint) => void;
  onRemoveFilter: (columnField: string) => void;
  onAdvancedSortClick: () => void;
  onHideColumn: (column: ITableColumn, hidden: boolean) => void;
  onColumnSelectionClick: (activeColumn: ITableColumn) => void;
  onToggleColumnHeadersWrapping: (wrapColumnHeader: boolean) => void;
  refreshPopover?: () => void; // when the width changes dynamically we need to tell the popover to refresh

  columnMenuData: IColumnMenuData;
  wrapColumnHeaders: boolean;
  tableQuerySpec: InputTableQuerySpec;
}

class ColumnMenuCmp extends PureComponent<IColumnMenuProps & InjectedFormProps<any, any>> {
  constructor(props) {
    super(props);

    this.handleFilterApply = this.handleFilterApply.bind(this);
    this.hideColumn = this.hideColumn.bind(this);
    this.createFilterMenuItem = this.createFilterMenuItem.bind(this);
    this.resetFilter = this.resetFilter.bind(this);
  }

  componentDidMount() {
    this.buildMenu(this.props);
  }

  componentDidUpdate(prevProps: IColumnMenuProps) {
    if (
      this.props.columnMenuData.menuTarget !== prevProps.columnMenuData.menuTarget ||
      this.props.columnMenuData.activeColumn !== prevProps.columnMenuData.activeColumn
    ) {
      this.buildMenu(this.props);
    }
  }

  buildMenu(props: IColumnMenuProps) {
    this.initializeFilter(props);
  }

  initializeFilter(props: IColumnMenuProps) {
    const activeColumn: ITableColumn = props.columnMenuData.activeColumn;

    if (!activeColumn.filterable) {
      return;
    }
    // initialization of the filter with default operator
    const initialFilterOperation: IRsqlComparisonExpr = {
      key: activeColumn.field,
      operator: activeColumn.searchAttributes.operators && activeColumn.searchAttributes.operators[0].key,
      value: undefined,
      value2: undefined,
    };
    if (activeColumn.searchAttributes.cellType === CELL_TYPE.DATE) {
      // dates are special cases, for some reason with the date-picker library the value needs to be
      // initialized if we provide a "format" property (see AdvancedSearchDate component)
      initialFilterOperation.value = parseDateToISO(new Date().toISOString()); // this method makes sure the date is the local date (search should not be TZ aware)
      initialFilterOperation.value2 = parseDateToISO(new Date().toISOString());
    }
    if (activeColumn.searchAttributes.cellType === CELL_TYPE.DATE_TIME) {
      initialFilterOperation.value = new Date().toISOString();
      initialFilterOperation.value2 = new Date().toISOString();
    }

    // in the advanced search panel we initialize also the BOOLEAN types to "true" but we don't want that in there

    // if a filterExpression is available we parse it and use it as initialization.
    const columnFilterOperation = parse(activeColumn.filterExpression); // direct per-column filter, if present
    const rowFilterOperation = parse(props.tableQuerySpec?.rowFilter) ?? undefined; // aggregated table filter (may include this column)
    const parsedFilterForField =
      columnFilterOperation ?? getFilterForField(rowFilterOperation, activeColumn.searchAttributes?.field ?? "");
    let filterOperation: RsqlFilterConstraint = parsedFilterForField || initialFilterOperation;
    //profile column filters can have up to two operators, this requires special preparation of the data structure
    filterOperation = this.preprocessFilterData(filterOperation);

    const init: any = {};
    // use a deep set to create automatically a hierarchy in the init object
    set(init, activeColumn.field, filterOperation);
    this.props.initialize(init);
  }

  /**
   * Profile filter can have up to two RSQL filter units.
   * Date Between filter has two values in .value as one array
   *
   * This method prepares the filter data for display with the stateless component so that
   * 1) present value array is filterOperation.value
   * 2) absent value array is filterOperation.value2
   * The cases to consider are:
   * - If there are two filter units, the parser returns a AStree with an CONSTRAINT_OPERATOR.AND root ad two children (absent and present).
   * - If there is only an =absent= filter, the values will end up in the wrong field.
   * - If there is only a =present= filter, the values will end up in the correct field (nothing to do).
   * @param filterOperation
   */
  preprocessFilterData = (filterOperation: RsqlFilterConstraint): RsqlFilterConstraint => {
    if (isIRsqlComparisonExpr(filterOperation) && filterOperation.operator === SEARCH_OPERATORS.BETWEEN) {
      const comparisonExpr: IRsqlComparisonExpr = filterOperation as IRsqlComparisonExpr;
      // between has two values in the .value[]
      filterOperation.value2 = comparisonExpr.value[1];
      filterOperation.value = comparisonExpr.value[0]; // order dependency
      return filterOperation;
    }
    if (
      isIRsqlFilterTree(filterOperation) &&
      Object.prototype.hasOwnProperty.call(filterOperation, CONSTRAINT_OPERATORS.AND) &&
      filterOperation[CONSTRAINT_OPERATORS.AND].length === 2 &&
      isProfileFilterColumn(filterOperation[CONSTRAINT_OPERATORS.AND][0].key)
    ) {
      filterOperation.value = filterOperation[CONSTRAINT_OPERATORS.AND][0].value;
      filterOperation.value2 = filterOperation[CONSTRAINT_OPERATORS.AND][1].value;
    }
    if (isProfileFilterColumn((filterOperation as IRsqlComparisonExpr).key)) {
      const castedRef: IRsqlComparisonExpr = filterOperation as IRsqlComparisonExpr;
      if (castedRef.operator === SEARCH_OPERATORS.ABSENT) {
        // if the filter contained only values that must be absent, they end up in the value field and must be swapped.
        castedRef.value2 = castedRef.value;
        castedRef.value = undefined;
        return castedRef;
      }
      if (castedRef.operator === SEARCH_OPERATORS.IN) {
        return undefined; // ignore the IN filter of the genome browser
      }
    }
    return filterOperation;
  };

  handleFilterApply = (values: any) => {
    const activeColumn: ITableColumn = this.props.columnMenuData.activeColumn;
    const field: string = activeColumn.searchAttributes.field;
    const operation: IRsqlComparisonExpr = get(values, field) as IRsqlComparisonExpr; // deep get because the redux-form values are built hierarchically if there are dots in the field name
    if (operation.value != null && !Array.isArray(operation.value)) {
      // if the value is an array then this ought to be controlled vocabulary.
      operation.value = operation.value.toString().trim();
    }

    if (isProfileFilterColumn(operation.key)) {
      this.handleProfileFilter(operation);
    } else {
      this.props.onFilter(field, operation);
    }
  };

  /**
   * Creates a small AST, if required, that represents a logical 'and' with two child RSQL filter units.
   * @param {IRsqlComparisonExpr} operation
   */
  handleProfileFilter = (operation: IRsqlComparisonExpr) => {
    const field: string = this.props.columnMenuData.activeColumn.searchAttributes.field;
    if (!isUndefinedOrEmptyArray(operation.value) && !isUndefinedOrEmptyArray(operation.value2)) {
      const twoOpFilter = {
        [CONSTRAINT_OPERATORS.AND]: [
          { key: operation.key, operator: SEARCH_OPERATORS.PRESENT, value: operation.value },
          { key: operation.key, operator: SEARCH_OPERATORS.ABSENT, value: operation.value2 },
        ],
      };
      this.props.onFilter(field, twoOpFilter);
    } else if (!isUndefinedOrEmptyArray(operation.value)) {
      this.props.onFilter(field, {
        key: operation.key,
        operator: SEARCH_OPERATORS.PRESENT,
        value: operation.value,
      });
    } else if (!isUndefinedOrEmptyArray(operation.value2)) {
      this.props.onFilter(field, {
        key: operation.key,
        operator: SEARCH_OPERATORS.ABSENT,
        value: operation.value2,
      });
    }
  };

  hideColumn() {
    this.props.onHideColumn(this.props.columnMenuData.activeColumn, true);
  }

  wrapColumnHeaders = () => {
    this.props.onToggleColumnHeadersWrapping(!this.props.wrapColumnHeaders);
  };

  isFilterMenuItemAvailable(): boolean {
    const { activeColumn } = this.props.columnMenuData;
    // for a filter menu to be available, the column must be filterable
    if (activeColumn.filterable) {
      const isVariantProfileColumn = activeColumn.cellType === CELL_TYPE.VARIANT_PROFILE;
      if (isVariantProfileColumn) {
        const isOrganismVariants = this.props.tableQuerySpec.queryType === ENTITY_TYPE.ORGANISM;
        if (isOrganismVariants) {
          const isDerivedVariants = this.props.tableQuerySpec.resultType === ENTITY_TYPE.VARIANT;
          if (isDerivedVariants) {
            // in case of derived variant profile column in an organism's variants table, the ref genome should also correspond to the parent genome
            return this.props.tableQuerySpec.id === this.props.tableQuerySpec.restrictions?.refGenomeId;
          }
        }
      }
      // all other cases return true;
      return true;
    }
    return false;
  }

  render() {
    const { activeColumn } = this.props.columnMenuData;
    const filterable = this.isFilterMenuItemAvailable();

    return (
      <MenuDialog
        topWidget={filterable && this.createFilterMenuItem(activeColumn)}
        className={"column-menu-dialog no-scroll"}
        aria-label={"Column menu"}
      >
        <MenuSection title={"Table configuration"}>
          <MenuItem id="column-menu-headers" onAction={() => this.wrapColumnHeaders()} icon={"minimize"}>
            {this.props.wrapColumnHeaders ? "Condense" : "Wrap"} Column Headers
          </MenuItem>
          <MenuItem
            id="column-menu-columns"
            onAction={() => this.props.onColumnSelectionClick(activeColumn)}
            icon={"view_column"}
          >
            Add or Remove Columns
          </MenuItem>
          {activeColumn.sortable && ( // add the advanced sorting option to the column dialog if the column is sortable
            <MenuItem id="column-menu-sort" onAction={() => this.props.onAdvancedSortClick()} icon={"sort_by_alpha"}>
              Advanced Sorting
            </MenuItem>
          )}
        </MenuSection>
        {!activeColumn.fixed && (
          <MenuSection title={"Column"} hideTitle>
            <MenuItem
              id="column-menu-hide"
              onAction={() => this.hideColumn()}
              icon={"visibility_off"}
              intent={MenuItemIntent.Danger}
            >
              Hide This Column
            </MenuItem>
          </MenuSection>
        )}
      </MenuDialog>
    );
  }

  // validation takes place to disable the Filter button, but error messages won't be shown, not required for a single input field
  validateFilterInput = (filterOperation: IRsqlComparisonExpr, _test, _props, _name) => {
    const activeColumn: ITableColumn = this.props.columnMenuData.activeColumn;
    return validateFilterOperation(filterOperation, activeColumn.cellType);
  };

  resetFilter() {
    const field: string = this.props.columnMenuData.activeColumn.searchAttributes.field;
    this.props.onRemoveFilter(field);
  }

  createFilterMenuItem(activeColumn: ITableColumn) {
    const searchAttribute = activeColumn.searchAttributes;
    const buttonDisabled = this.props.invalid; /*|| this.props.pristine*/

    return (
      <form
        id="filter-submit-form"
        className={clsx("form-column", style.top)}
        onSubmit={this.props.handleSubmit(this.handleFilterApply)}
      >
        <AdvancedSearchCriteriaReduxFormField
          key={searchAttribute.field}
          name={searchAttribute.field}
          noLabel={false}
          noErrorMessage
          verticalLayout
          validate={this.validateFilterInput}
          searchAttribute={searchAttribute}
          tableQuerySpec={this.props.tableQuerySpec}
          onOperatorChange={this.props.refreshPopover}
          component={AdvancedSearchCriteria as any}
          condensedView
          autoFocus
        />
        <div className={style.buttons}>
          <ActionButton
            intent={ActionButtonIntent.Secondary}
            onPress={() => this.resetFilter()}
            size={ActionButtonSize.Small}
          >
            Reset
          </ActionButton>
          <ActionButton
            intent={ActionButtonIntent.Primary}
            type={"submit"}
            isDisabled={buttonDisabled}
            size={ActionButtonSize.Small}
          >
            Filter
          </ActionButton>
        </div>
      </form>
    );
  }
}

const ColumnContextualMenu = reduxForm<{}, IColumnMenuProps>({
  form: COLUMN_FILTER_FORM,
})(ColumnMenuCmp);

export { ColumnMenuOverlay as ColumnMenu };
